import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { NOT_LOADED_STATE } from "src/common/ApiCall";
import { PEV } from "src/common/utils";
import { ProgressErrorValue } from "src/common/utils/progress-error-value";
import { fetchAllResources } from "../service/resources";
import { Nullable } from "../../../common/utils/nullable";

export class DisplayUserDetails {
  oktaUserId: String;
  email: String;
  employeeId: Number;
  fullName: String;

  constructor(owner: DisplayUserDetails) {
    this.oktaUserId = owner.oktaUserId;
    this.email = owner.email;
    this.employeeId = owner.employeeId;
    this.fullName = owner.fullName;
  }
}

export class GitsContact {
  name: Nullable<string>;
  email: string;
  type: "TEAM" | "INDIVIDUAL";

  constructor(gitsContact: GitsContact) {
    this.name = gitsContact.name;
    this.email = gitsContact.email;
    this.type = gitsContact.type;
  }
}

export class Resource {
  id: String;
  name: String;
  group: String;
  domain: String;
  attributes: Record<string, string>;
  owners: DisplayUserDetails[] | null;
  gitsContacts: Nullable<GitsContact[]>;
  belongsToUser: Boolean;

  constructor(resource: Resource) {
    this.id = resource.id;
    this.name = resource.name;
    this.group = resource.group;
    this.domain = resource.domain;
    this.attributes = resource.attributes;
    this.owners = resource.owners?.map((owner) => new DisplayUserDetails(owner)) ?? null;
    this.belongsToUser = resource.belongsToUser;
    this.gitsContacts = resource.gitsContacts;
  }

  ownersName(): String[] | null {
    return this.owners?.map((owner) => owner.fullName) ?? null;
  }
}

export class Resources {
  resources: Resource[];

  constructor(resources: Resource[]) {
    this.resources = resources.map((resource) => new Resource(resource));
  }

  isResourceEmpty() {
    return this.resources.length === 0;
  }

  getResources(): Resource[] {
    return this.resources;
  }
}

export interface ResourcesReduxState {
  resourcesPEV: ProgressErrorValue<Resources>;
}

const initialState: ResourcesReduxState = {
  // @ts-ignore
  resourcesPEV: PEV([], NOT_LOADED_STATE),
};

const getAllResources = createAsyncThunk("resources/fetch", async () => {
  return await fetchAllResources();
});

const ResourcesSlice = createSlice({
  name: "resources",
  initialState,
  reducers: {
    resetResources(state) {
      state.resourcesPEV = initialState.resourcesPEV;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllResources.pending, (state) => {
        // @ts-ignore
        state.resourcesPEV = PEV(
          state.resourcesPEV.value, // @ts-ignore
          "Fetching resources",
        );
      })
      .addCase(getAllResources.fulfilled, (state, action) => {
        state.resourcesPEV = PEV(action.payload);
      })
      .addCase(getAllResources.rejected.type, (state, action) => {
        // @ts-ignore
        state.resourcesPEV = PEV(
          state.resourcesPEV.value,
          null, // @ts-ignore
          "Error while fetching resources",
        );
      });
  },
});

const { resetResources } = ResourcesSlice.actions;
export const ResourcesReducer = ResourcesSlice.reducer;

export const ResourcesActions = {
  getAllResources,
  resetResources,
};
